import { NumberObjectHelper } from '@attain-apps/object-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as core from '../../../core'
import * as common from '../../common'
import type { RowBasedReport } from '../row-based-report'

import { Pool } from './pool'

/**
 * Represents an allocation of costs, usually from one
 * {@link common.FaFunction.Category} to another
 *
 * These costs tend to represent a sort of hidden cost for some other activity.
 * For example, you may drive to a grocery store and spend $100 on groceries.
 * However, in order to do so, you may have had to drive 5 miles. In those 5
 * miles, you used up some of your car's gas and oil, wore down the brake pads,
 * battery, and other components; and all of these things cost some amount of
 * money. So in addition to the explicit/natural amount of $100 you spent on
 * groceries, you also had some hidden costs related to gas and other car
 * expenses. Those hidden costs could be represented as an allocation from your
 * "car" category of expenses to your "groceries" category of expenses
 *
 * The typical process for determining an allocation is:
 * 1. Identify a certain
 *    {@link Allocation.ValueType.Statistic allocation statistic} for each
 *    {@link common.FaFunction.Category} that this
 *    {@link common.FaFunction.Category} can allocate to. This statistic usually
 *    represents an estimate of how an item is used in support of particular
 *    activity. E.g., in the above example, miles could be used to estimate how
 *    your car is being used. If in a year, you drive 10,000 miles and 500 of
 *    those miles were to get groceries, you could estimate that about 500 /
 *    10,000 or 5% of your car expenses for that year should be allocated to
 *    groceries. In Rate, square feet and dollars are common types of statistics
 * 1. Take each of those statistics and divide them by the sum of those
 *    statistics to determine
 *    {@link Allocation.ValueType.Percent allocation percents}. This is how we
 *    got to 5% in the previous step's example
 * 1. Take each of those percents and multiply them by the cost pool available
 *    to allocate to determine the
 *    {@link Allocation.ValueType.Currency allocation dollar amounts}. Following
 *    through with the previous example, if you spent a total of $2,000 on gas,
 *    oil changes, repairs, and anything else related to your car, then the
 *    allocation of your car expenses to your groceries is 5% * $2,000 = $100.
 *    In other words, if you spent exactly $5,000 on groceries that year, then
 *    your full cost to get groceries was actually $5,100 when you include the
 *    cost of you driving to get the groceries
 *
 * This process can be done for an entire {@link common.FaFunction.Category} at
 * once, or by a particular unit; e.g., allocating the cost pools of each
 * department
 *
 * __NOTE:__ while the above process represents the typical usage of this type,
 * the structure can be used for other use cases that require each of the three
 * {@link Allocation.ValueType value types} mentioned above
 */
export type Allocation = Record<Allocation.ValueType, number>
export namespace Allocation {
  /**
   * Represents different values of an {@link Allocation}
   */
  export enum ValueType {
    /**
     * Dollars that are being allocated
     */
    Currency = '$',
    /**
     * Some number that was used to determine the {@link Percent}
     *
     * This can be of any unit (e.g. dollars, square footage, number of
     * students, etc.)
     */
    Statistic = '#',
    /**
     * Percent of the corresponding cost pool that is being allocated
     *
     * Multiplying that cost pool by this value gives you the {@link Currency}
     */
    Percent = '%',
  }
  // Needs to be before namespace is defined
  const VALUE_TYPES: ReadonlySet<ValueType> = new Set(
    Object.values(ValueType) as ValueType[],
  )
  export namespace ValueType {
    export const ALL: readonly ValueType[] = Array.from(VALUE_TYPES)
    export const isValueType = TypeChecker.Factory.oneOf(VALUE_TYPES)
    export const assertIsValueType: TypeAsserter<ValueType> =
      TypeAsserter.Factory.from(isValueType)

    // NOTE: we expose this through a function, rather than directly exporting,
    // as that allows us to encapsulate, and potentially change the logic easier
    const COMMON_VALUE_TYPE_LOOKUP = {
      [ValueType.Currency]: core.common.value.types.Intrinsic.Currency,
      [ValueType.Percent]: core.common.value.types.Intrinsic.Percent,
      [ValueType.Statistic]: core.common.value.types.Intrinsic.Decimal,
    } as const satisfies Record<ValueType, core.common.value.types.Type>
    /**
     * Gets the {@link common.value.types.Type} for a {@link Allocation.ValueType}
     */
    export function toCommonValueType<TValueType extends ValueType>(
      valueType: TValueType,
    ): (typeof COMMON_VALUE_TYPE_LOOKUP)[TValueType] {
      return COMMON_VALUE_TYPE_LOOKUP[valueType]
    }

    // NOTE: we expose this through a function, rather than directly exporting,
    // as that allows us to encapsulate, and potentially change the logic easier
    const DEFAULT_PRECISION_LOOKUP: Record<ValueType, number> = {
      [ValueType.Percent]: 1,
      [ValueType.Currency]: 0,
      [ValueType.Statistic]: 0,
    }
    /**
     * Gets the default {@link common.value.precision} for a
     * {@link Allocation.ValueType}
     */
    export function getDefaultPrecision(valueType: ValueType): number {
      return DEFAULT_PRECISION_LOOKUP[valueType]
    }
  }

  /**
   * Represents an {@link Allocation} that can be presented as a row in a report
   *
   * Structured as a {@link RowBasedReport.Entry} where the values are an
   * {@link Allocation}. This does not necessarily mean it must be used in a
   * {@link RowBasedReport}
   */
  export type Entry = RowBasedReport.Entry<Allocation>

  /**
   * Contains {@link Allocation} helper functions
   *
   * @see {@link Helper.INSTANCE}
   */
  export class Helper extends NumberObjectHelper<Allocation> {
    /**
     * Singleton of {@link Helper}
     */
    public static INSTANCE: Helper = new Helper()

    private constructor() {
      super({
        /**
         * This *must* be more precise for {@link ValueType.Percent} and
         * potentially {@link ValueType.Statistic}
         */
        irrelevancePrecision: 1e3,
        keys: ValueType.ALL,
      })
    }

    /**
     * An empty/all-zero {@link Allocation}
     */
    public override zero(): Allocation {
      return {
        [ValueType.Currency]: 0,
        [ValueType.Percent]: 0,
        [ValueType.Statistic]: 0,
      }
    }

    /**
     * Override allows the value to be 0 which was not originally the case.
     *
     * @see {@link NumberObjectHelper.isIrrelevantValue}
     */
    protected override isIrrelevantValue(val: unknown): boolean {
      return (
        TypeChecker.isNullish(val) ||
        (TypeChecker.isNumber(val) && val !== 0 && this.isIrrelevantNumber(val))
      )
    }
  }

  /**
   * Represents a series of {@link Allocation allocations} (in the form of
   * {@link ByFaCategory})
   *
   * Often this type is extended or intersected to have additional properties
   *
   * @see {@link Distribution.OfPool}
   */
  export interface Distribution {
    /**
     * @see {@link ByFaCategory}
     */
    allocations: ByFaCategory
  }
  export namespace Distribution {
    /**
     * Creates a {@link Distribution} using
     * {@link Allocation.ByFaCategory.Statistics}
     *
     * @see {@link Allocation.ByFaCategory.from}
     */
    export function from(
      params: Readonly.Deep<ByFaCategory.from.Params>,
    ): Distribution {
      return { allocations: ByFaCategory.from(params) }
    }

    /**
     * Creates a {@link Distribution} that represents the total of a list of
     * {@link Distribution}
     */
    export function combine(
      ...distributions: readonly Readonly.Deep<Distribution>[]
    ): Distribution {
      return {
        allocations: Lookup.combine(
          ...distributions.map(({ allocations }) => allocations),
        ),
      }
    }

    /**
     * Creates a copy of the given {@link Distribution}, except where the
     * {@link ValueType.Percent} values of {@link Allocation}s have been
     * recalculated to be proportional to either the {@link ValueType.Currency}
     * or {@link ValueType.Statistic} values (use
     * {@link recalculatePercent.Options.using} to determine which)
     */
    export function recalculatePercent(
      { allocations }: Readonly<Distribution>,
      { using = ValueType.Currency }: Lookup.recalculatePercent.Options = {},
    ): Distribution {
      return {
        allocations: Lookup.recalculatePercent(allocations, {
          using,
        }),
      }
    }

    export namespace combine {
      /**
       * When combining a series of {@link Distribution} that are allocating
       * different amounts, the statistics may no longer be proportional to the
       * percents & currency. If having statistics that are proportional to the
       * percents & currency is desired, this function can be used to get a
       * result where the {@link ValueType.Currency} value is used for the
       * {@link ValueType.Statistic}.
       *
       * @example
       * // Allocates $100 (100%) to INST
       * const distribution1: Allocation.Distribution = {
       *   allocations: {
       *     '!INST': { ['#']: 1, ['%']: 1, ['$']: 100 }
       *   }
       * }
       * // Allocates $900 (100%) to OR
       * const distribution2: Allocation.Distribution = {
       *   allocations: {
       *     '!OR': { ['#']: 1, ['%']: 1, ['$']: 900 }
       *   }
       * }
       * // Allocates $100 (10%) to INST and $900 (90%) to OR
       * const desiredTotalDistribution = Allocation.Distribution.combine.withCurrencyForStatistics(distribution1, distribution2)
       * // ===
       * const desiredTotalDistribution = {
       *   '!INST': { ['#']: 100, ['%']: 0.1, ['$']: 100 }
       *   '!OR': { ['#']: 900, ['%']: 0.9, ['$']: 900 }
       * }
       * // Same as previous, but with disproportionate statistics
       * const undesiredTotalDistribution = Allocation.Distribution.combine(distribution1, distribution2)
       * // ===
       * const undesiredTotalDistribution = {
       *   '!INST': { ['#']: 1, ['%']: 0.1, ['$']: 100 }
       *   '!OR': { ['#']: 1, ['%']: 0.9, ['$']: 900 }
       * }
       */
      export function withCurrencyForStatistics(
        ...distributions: readonly Readonly.Deep<Distribution>[]
      ): Distribution {
        const totalDistribution = combine(...distributions)
        for (const allocation of Object.values(totalDistribution.allocations)) {
          if (allocation) {
            allocation['#'] = allocation.$
          }
        }
        return totalDistribution
      }
    }

    /**
     * Returns the total {@link Allocation}, representing everything allocated
     * in {@link Distribution.allocations}
     *
     * @see {@link ByFaCategory.getTotalAllocation}
     */
    export function getTotalAllocation(
      distribution: Readonly.Deep<Distribution>,
    ): Allocation {
      return Lookup.getTotalAllocation(distribution.allocations)
    }

    /**
     * Represents a {@link Distribution} associated with a
     * {@link dms.data.Record}
     *
     * Structured as a {@link RowBasedReport.Entry} where the values are a
     * {@link Distribution}. This does not necessarily mean it must be used in a
     * {@link RowBasedReport}, but it is structured so that it can
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Entry = core.common.Identified<core.dms.data.Record.Id> &
      RowBasedReport.Entry<Distribution>

    /**
     * Simply a {@link Distribution} with
     * {@link common.FaFunction.Categorization.Ids}
     *
     * Useful when you need some way to match one or more {@link Distribution}
     * with their corresponding {@link common.FaFunction.Category} or
     * {@link common.FaFunction.Subcategory}
     */
    export type WithCategorizationIds = Distribution &
      common.FaFunction.Categorization.Ids

    /**
     * The final {@link Distribution} of a {@link Pool},
     * including the {@link Pool.TotalWithCrossAllocations.naturalPoolAmount} and
     * {@link Pool.TotalWithCrossAllocations.crossAllocations}
     */
    export type OfPool = Pool.TotalWithCrossAllocations & Distribution
    export namespace OfPool {
      /**
       * When looking at a {@link Distribution.OfPool}, there are often multiple
       * {@link Distribution} that represent various aspects of that
       * {@link Distribution.OfPool}
       *
       * For example, one could look at a {@link Distribution} that only
       * allocates the {@link Distribution.OfPool.naturalPoolAmount} or one that
       * also includes {@link Distribution.OfPool.crossAllocations} (this latter
       * option is what is represented in
       * {@link Distribution.OfPool.allocations}). One could also consider this
       * {@link Distribution} of each associated {@link Pool.Subpool}
       *
       * This type represents a list of useful {@link Distribution} (in the form
       * of {@link Breakdown.Entry}) all corresponding to a single {@link Pool}
       * (represented by {@link Breakdown.id})
       *
       * As a note, the contents of this list can vary, so this is really
       * intended for displaying to a user rather than consuming
       * programmatically
       */
      export interface Breakdown extends core.common.Identified<Pool.Id> {
        /**
         * A list of {@link Breakdown.Entry}, representing various informative
         * {@link Distribution} corresponding to the associated {@link Pool}
         */
        entries: readonly Breakdown.Entry[]
      }
      export namespace Breakdown {
        /**
         * A single {@link Distribution} in a
         * {@link Distribution.OfPool.Breakdown}
         */
        // eslint-disable-next-line @typescript-eslint/no-shadow
        export interface Entry extends core.common.Named, Distribution {
          /**
           * Represents total amount being allocated. Ideally, this is the same
           * as the {@link Allocation.ValueType.Currency dollar} value in
           * {@link Distribution.getTotalAllocation} for this
           * {@link Distribution}. However, sometimes data or calculation errors
           * lead to that not being the case. For that reason, it can be useful
           * for users to compare this value to
           * {@link Distribution.getTotalAllocation}
           */
          allocableAmount: number
        }
      }
    }
  }

  /**
   * Used to retrieve an {@link Allocation} by some key
   *
   * @see {@link Allocation.ByFaCategory}
   */
  export type Lookup<K extends string> = Partial<Record<K, Allocation>>
  export namespace Lookup {
    /**
     * Creates a {@link Lookup} that represents the total of a list of
     * {@link Lookup}
     */
    export function combine<
      TLookups extends readonly Readonly.Deep<Lookup<any>>[],
    >(...lookups: TLookups): TLookups[number]
    export function combine(
      ...lookups: readonly Lookup<string>[]
    ): Lookup<string> {
      const combinedLookup: Lookup<string> = {}
      for (const lookup of lookups) {
        for (const k in lookup) {
          const allocation = lookup[k]
          if (!allocation) {
            continue
          }
          const allocationOnCombinedLookup = combinedLookup[k]
          if (allocationOnCombinedLookup) {
            combinedLookup[k] = Helper.INSTANCE.combine(
              allocationOnCombinedLookup,
              allocation,
            )
          } else {
            combinedLookup[k] = { ...allocation }
          }
        }
      }
      // Recalculates the percents to total to 100%. For example, if we have two
      // lookups and both allocate 100% to OR, by default the combined percent
      // will be 200%. Instead, we want to take the total dollars allocated to
      // OR and divide it by the total dollars allocated, which in this case
      // will give us 100%
      return recalculatePercent(combinedLookup)
    }

    /**
     * Returns all non-nullish entries of the given {@link Allocation.Lookup}
     *
     * Similar to {@link Object.entries}
     */
    export function entries<TLookup extends Readonly<Lookup<any>>>(
      lookup: TLookup,
    ): TLookup extends Lookup<infer K> ? [K, NonNullable<TLookup[K]>][] : never
    export function entries(
      lookup: Readonly<Lookup<string>>,
    ): [string, Allocation][] {
      return Object.entries(lookup).filter(
        (
          entry,
        ): entry is typeof entry &
          [(typeof entry)[0], NonNullable<(typeof entry)[1]>] =>
          TypeChecker.isNotNullish(entry[1]),
      )
    }

    /**
     * Creates a copy of the given {@link Lookup}, except where the
     * {@link ValueType.Percent} values have been recalculated to be
     * proportional to either the {@link ValueType.Currency} or
     * {@link ValueType.Statistic} values (use
     * {@link recalculatePercent.Options.using} to determine which)
     *
     * This function guarantees that the total {@link ValueType.Percent} in the
     * {@link Lookup} will either be `100%` or `0%`, and it is only `0%` when
     * all of the {@link ValueType.Currency} or {@link ValueType.Statistic}
     * values are `0`.
     */
    export function recalculatePercent<TLookup extends Lookup<any>>(
      lookup: TLookup,
      { using = ValueType.Currency }: recalculatePercent.Options = {},
    ): TLookup {
      const recalculatedLookup: TLookup = { ...lookup }
      // We need to sum up the 'using' values to get a denominator
      let denominator = 0
      for (const k in lookup) {
        denominator += lookup[k]?.[using] ?? 0
      }
      for (const k in lookup) {
        const allocation = lookup[k]
        if (!allocation) {
          // If this property is undefined in the source, we can delete it from
          // the result
          delete recalculatedLookup[k]
          continue
        }
        // Otherwise, we create a copy and recalculate the percent in that copy
        recalculatedLookup[k] = {
          ...allocation,
          '%': denominator === 0 ? 0 : allocation[using] / denominator,
        }
      }
      return recalculatedLookup
    }
    export namespace recalculatePercent {
      export interface Options {
        /**
         * Calculated percents will be proportional to this {@link ValueType}
         *
         * @default ValueType.Currency
         */
        using?: Exclude<ValueType, ValueType.Percent>
      }
    }

    /**
     * Returns the total {@link Allocation} by summing the values in the given
     * {@link Lookup}
     */
    export function getTotalAllocation(
      allocations: Readonly<Lookup<string>>,
    ): Allocation {
      return Helper.INSTANCE.combine(
        ...Object.values(allocations).filter(TypeChecker.isNotNullish.raw),
      )
    }
  }

  /**
   * Used to lookup {@link Allocation} by {@link common.FaFunction.Category.Id}
   *
   * Mainly used in {@link Distribution}
   *
   * @example
   * const allocations: Allocation.ByFaCategory = getAllocations()
   * const omAllocation = allocations['!OM']
   */
  export type ByFaCategory = Lookup<common.FaFunction.Category.Id>
  export namespace ByFaCategory {
    /**
     * Represents just the {@link Allocation.ValueType.Statistic} values for
     * {@link Allocation.ByFaCategory}
     *
     * @see {@link from}
     */
    export type Statistics = Partial<
      Record<common.FaFunction.Category.Id, number>
    >
    export namespace Statistics {
      /**
       * Extracts the {@link Statistics} of a {@link Distribution}
       */
      // NOTE: this could accept Distribution or ByFaCategory, but receiving
      // Distribution is probably more common, plus it is easy to create a
      // Distribution from a ByFaCategory
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function from(
        distribution: Readonly.Deep<Distribution>,
        { using = ValueType.Currency }: from.Options = {},
      ): Statistics {
        const statistics: Statistics = {}
        for (const k in distribution.allocations) {
          const categoryId = k as keyof typeof distribution.allocations
          statistics[categoryId] = distribution.allocations[categoryId]?.[using]
        }
        return statistics
      }
      export namespace from {
        export interface Options {
          /**
           * Extracts the {@link Statistics} for the given {@link ValueType}
           *
           * @default ValueType.Statistic
           */
          using?: ValueType
        }
      }

      /**
       * Returns the total of all the values in the given {@link Statistics}
       */
      export function getTotal(statistics: Readonly<Statistics>): number {
        return Object.values(statistics).reduce<number>(
          (total, statistic) => total + (statistic ?? 0),
          0,
        )
      }
    }

    /**
     * Generates {@link Allocation.ByFaCategory} using the given
     * {@link Statistics}
     *
     * @see {@link from.Params}
     */
    export function from({
      amountToAllocate,
      statistics,
    }: Readonly.Deep<from.Params>): ByFaCategory {
      const allocations: ByFaCategory = {}
      const totalStatistic = Statistics.getTotal(statistics)
      for (const k in statistics) {
        const categoryId = k as keyof typeof statistics
        const statistic = statistics[categoryId]
        if (statistic === undefined) {
          // Skip missing statistics
          continue
        }
        // Compute the values
        const percent = totalStatistic === 0 ? 0 : statistic / totalStatistic
        const amount = percent * amountToAllocate
        // Create the allocation
        allocations[categoryId] = {
          '#': statistic,
          $: amount,
          '%': percent,
        }
      }
      return allocations
    }
    export namespace from {
      export interface Params {
        amountToAllocate: number
        statistics: Statistics
      }
    }
  }

  /**
   * Some values that explain, or provide support for, an
   * {@link Allocation.Distribution.OfPool}
   *
   * Different {@link common.FaFunction.Category IDC categories} use different
   * {@link Support.Type}
   */
  export type Support<TType extends Support.Type = Support.Type> =
    // NOTE: distributing over TType allows this to be a discriminated union
    TType extends any
      ? Support.WithSubpools<TType> | Support.WithoutSubpools<TType>
      : never
  export namespace Support {
    /**
     * Properties that are common to both {@link WithSubpools} and
     * {@link WithoutSubpools}
     */
    interface Base<TType extends Type> extends core.common.Identified<Pool.Id> {
      type: TType
    }
    /**
     * {@link Pool} that has {@link Subpool subpools}
     *
     * @see {@link WithoutSubpools}
     */
    export interface WithSubpools<TType extends Type = Type>
      extends Base<TType> {
      /**
       * @see {@link Subpool}
       */
      // NOTE: intentionally not readonly to allow for iterative creation
      subpools: ForSubpool<TType>[]
    }
    /**
     * {@link Pool} that does NOT have {@link Subpool subpools}
     *
     * @see {@link WithSubpools}
     */
    export type WithoutSubpools<TType extends Type = Type> = Base<TType> &
      Data<TType>

    /**
     * Determines if a {@link Pool} is {@link WithSubpools}
     *
     * If it is more convenient for type purposes, the negation,
     * {@link hasNoSubpools}, can also be used
     */
    // NOTE: the overloads handle a `Readonly.Deep` version of `Pool`
    export function hasSubpools<TType extends Type>(
      support: Support<TType>,
    ): support is Extract<Support<TType>, WithSubpools<TType>>
    export function hasSubpools<TType extends Type>(
      support: Readonly.Deep<Support<TType>>,
    ): support is Extract<
      Readonly.Deep<Support<TType>>,
      Readonly.Deep<WithSubpools<TType>>
    >
    export function hasSubpools(
      support: Readonly.Deep<Support>,
    ): support is Extract<Readonly.Deep<Support>, Readonly.Deep<WithSubpools>> {
      return !!(support as FlattenUnion<typeof support>).subpools
    }

    /**
     * Determines if a {@link Pool} is {@link WithoutSubpools}
     *
     * If it is more convenient for type purposes, the negation,
     * {@link hasSubpools}, can also be used
     */
    // NOTE: the overloads handle a `Readonly.Deep` version of `Pool`
    export function hasNoSubpools<TType extends Type>(
      support: Support<TType>,
    ): support is Extract<Support<TType>, WithoutSubpools<TType>>
    export function hasNoSubpools<TType extends Type>(
      support: Readonly.Deep<Support<TType>>,
    ): support is Extract<
      Readonly.Deep<Support<TType>>,
      Readonly.Deep<WithoutSubpools<TType>>
    >
    export function hasNoSubpools(
      support: Readonly.Deep<Support>,
    ): support is Extract<
      Readonly.Deep<Support>,
      Readonly.Deep<WithoutSubpools>
    > {
      return !hasSubpools(support)
    }
    /**
     * Returns the total allocable amount from the given {@link Pool} of the
     * given {@link Type}
     *
     * @see {@link Values.getAllocableAmount}
     */
    // NOTE: the overloads handle a `Readonly.Deep` version of `WithSubpools`
    export function getSubpool<TType extends Type>(
      support: WithSubpools<TType>,
      subpoolId: Pool.Subpool.Id,
    ): ForSubpool<TType> | undefined
    export function getSubpool<TType extends Type>(
      support: Readonly.Deep<WithSubpools<TType>>,
      subpoolId: Pool.Subpool.Id,
    ): Readonly.Deep<ForSubpool<TType>> | undefined
    export function getSubpool<TType extends Type>(
      support: Readonly.Deep<WithSubpools<TType>>,
      subpoolId: Pool.Subpool.Id,
    ): Readonly.Deep<ForSubpool<TType>> | undefined {
      return support.subpools.find(subpool => subpool.id === subpoolId)
    }

    /**
     * Gets the total {@link Distribution} represented by the given
     * {@link Support}
     *
     * @see {@link Support.Data.getTotalDistribution}
     */
    export function getTotalDistribution(
      support: Readonly.Deep<Support>,
    ): Distribution {
      if (hasSubpools(support)) {
        return Distribution.combine.withCurrencyForStatistics(
          ...support.subpools.map(subpool =>
            Data.getTotalDistribution(support.type, subpool),
          ),
        )
      }
      return Data.getTotalDistribution(support.type, support)
    }

    /**
     * {@link Allocation.Support.Data} for a specific {@link Pool.Subpool} in a
     * {@link Allocation.Support.WithSubpools}
     */
    export type ForSubpool<TType extends Support.Type = Support.Type> =
      core.common.Identified<Pool.Subpool.Id> & Data<TType>
    export namespace ForSubpool {
      /**
       * Gets the total {@link Distribution} represented by the given
       * {@link Support.ForSubpool} of the given {@link Support.Type}
       *
       * @see {@link Support.getTotalDistribution}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function getTotalDistribution<TType extends Support.Type>(
        type: TType,
        supportForSubpool: Readonly.Deep<ForSubpool<TType>>,
      ): Distribution {
        return Data.getTotalDistribution(type, supportForSubpool)
      }
    }

    /**
     * Essential information for {@link Allocation.Support}
     *
     * @see {@link Values}
     */
    export interface Data<TType extends Support.Type = Support.Type> {
      values: Values<TType>
    }
    export namespace Data {
      /**
       * Gets the total {@link Distribution} represented by the given
       * {@link Support.Data} of the given {@link Support.Type}
       *
       * @see {@link Values.getTotalDistribution}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function getTotalDistribution<TType extends Support.Type>(
        type: TType,
        data: Readonly.Deep<Data<TType>>,
      ): Distribution {
        return Values.getTotalDistribution(type, data.values)
      }
    }

    /**
     * {@link Allocation.Support} is mostly made up of these values, which are mostly numerical
     *
     * Thse values vary by {@link Support.Type}
     */
    export type Values<TType extends Support.Type = Support.Type> = {
      [Support.Type.Da]: Support.UsingDa.Values
      [Support.Type.Entries]: Support.UsingEntries.Values
      [Support.Type.Gl]: Support.UsingGl.Values
      [Support.Type.Lib]: Support.UsingLib.Values
      [Support.Type.None]: Support.UsingNone.Values
    }[TType]
    export namespace Values {
      /**
       * Gets the total {@link Distribution} represented by the given
       * {@link Support.Values} of the given {@link Support.Type}
       *
       * @see {@link Support.getTotalDistribution}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function getTotalDistribution<TType extends Type>(
        type: TType,
        // NOTE: we should only need the Readonly.Deep version, but for some
        // reason if we don't put the first type in this union, TypeScript
        // thinks this signature isn't compatible with the implementation
        // signature
        values: Values<TType> | Readonly.Deep<Values<TType>>,
      ): Distribution
      export function getTotalDistribution(
        type: Type,
        values: Readonly.Deep<Values>,
      ): Distribution {
        switch (type) {
          case Type.Da:
            return UsingDa.getTotalDistribution(values as Values<typeof type>)
          case Type.Entries:
            return UsingEntries.getTotalDistribution(
              values as Values<typeof type>,
            )
          case Type.Gl:
            return UsingGl.getTotalDistribution(values as Values<typeof type>)
          case Type.Lib:
            return UsingLib.getTotalDistribution(values as Values<typeof type>)
          case Type.None:
            return UsingNone.getTotalDistribution(values as Values<typeof type>)
          default:
            throw TypeAsserter.assertIsNever(type)
        }
      }
    }

    /**
     * Different {@link common.FaFunction.Category IDC categories} have
     * different relevant {@link Support}, depending on how they are calculated
     * (which is largely defined by the government). These are the different
     * types of support that can be used
     */
    export enum Type {
      /** @see {@link UsingNode} */
      None = 'none',
      /** @see {@link UsingEntries} */
      Entries = 'entries',
      /** @see {@link UsingGl} */
      Gl = 'gl',
      /** @see {@link UsingLib} */
      Lib = 'lib',
      /** @see {@link UsingDa} */
      Da = 'da',
    }
    // Needs to be before namespace is defined
    const TYPES: ReadonlySet<Type> = new Set(Object.values(Type) as Type[])
    export namespace Type {
      export const ALL: readonly Type[] = Array.from(TYPES)
      export const isType = TypeChecker.Factory.oneOf(TYPES)
      export const assertIsType: TypeAsserter<Type> =
        TypeAsserter.Factory.from(isType)

      /**
       * Returns the appropriate {@link Allocation.Support.Type} for a given
       * {@link common.FaFunction.Category.Id}
       */
      export function of(categoryId: common.FaFunction.Category.Id): Type {
        switch (categoryId) {
          case common.FaFunction.Category.Standard.Id.of(
            'BuildingDepreciation',
          ):
          case common.FaFunction.Category.Standard.Id.of(
            'EquipmentDepreciation',
          ):
          case common.FaFunction.Category.Standard.Id.of('Interest'):
          case common.FaFunction.Category.Standard.Id.of(
            'OperationsAndMaintenance',
          ):
            return Type.Entries
          case common.FaFunction.Category.Standard.Id.of(
            'GeneralAdministration',
          ):
          case common.FaFunction.Category.Standard.Id.of(
            'StudentAdministrationAndServices',
          ):
            return Type.None
          case common.FaFunction.Category.Standard.Id.of(
            'SponsoredProjectsAdministration',
          ):
            return Type.Gl
          case common.FaFunction.Category.Standard.Id.of('Library'):
            return Type.Lib
          case common.FaFunction.Category.Standard.Id.of(
            'DepartmentAdministration',
          ):
            return Type.Da
          default:
            // NOTE: if we ever support calculations for custom IDC categories,
            // we should think about what goes here
            return Type.None
        }
      }
    }

    /**
     * In some cases, no information is needed beyond the overall
     * {@link Allocation.Distribution}
     */
    export type UsingNone = Support<Type.None>
    export namespace UsingNone {
      /**
       * Just a {@link Allocation.Distribution}
       */
      export type Values = Distribution & {
        // NOTE: this is just needed for the reports
        amountToAllocate: number
      }

      /**
       * Gets the total {@link Distribution} represented by the given
       * {@link Support.UsingNone.Values}
       *
       * @see {@link Support.Values.getTotalDistribution}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function getTotalDistribution(
        distribution: Readonly.Deep<Values>,
      ): Distribution {
        return distribution
      }
    }

    /**
     * In many cases, to support the overall {@link Allocation.Distribution},
     * we use a list of {@link Allocation.Distribution.Entry}
     */
    export type UsingEntries = Support<Type.Entries>
    export namespace UsingEntries {
      /**
       * Contains {@link Values.entries} corresponding to a
       * {@link Values.entity}
       */
      export interface Values {
        /**
         * If the {@link entity} is defined, then each {@link Entry} of
         * {@link entries} is a record of this {@link dms.models.Entity}
         */
        entity?: core.dms.models.Entity.Id
        /**
         * These {@link Entry entries} show {@link Allocation.Distribution} per
         * record of {@link entity}
         */
        entries: readonly Distribution.Entry[]
      }

      /**
       * Gets the total {@link Distribution} represented by the given
       * {@link Support.UsingEntries.Values}
       *
       * @see {@link Support.Values.getTotalDistribution}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function getTotalDistribution({
        entries,
      }: Readonly.Deep<Values>): Distribution {
        return Distribution.combine(...entries.map(entry => entry.values))
      }
    }

    /**
     * In some cases, we need {@link Pool.OfGl} information, where each
     * {@link Pool.OfGl.Entry} represents the total {@link Pool.OfGl.Values}
     * of a {@link common.FaFunction.Category} that is being allocated to
     */
    export type UsingGl = Support<Type.Gl>
    export namespace UsingGl {
      /**
       * Each {@link Pool.OfGl.Entry} represents the total
       * {@link Pool.OfGl.Values} of a {@link common.FaFunction.Category} that
       * is being allocated to
       *
       * @see {@link UsingGl}
       */
      export type Values = Pool.OfGl.WithoutSubpools &
        Pick<ByFaCategory.from.Params, 'amountToAllocate'>

      /**
       * Gets the total {@link Distribution} represented by the given
       * {@link Support.UsingGl.Values}
       *
       * @see {@link Support.Values.getTotalDistribution}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function getTotalDistribution({
        amountToAllocate,
        entries,
      }: Readonly.Deep<Values>): Distribution {
        const statistics: ByFaCategory.Statistics = {}
        for (const entry of entries) {
          statistics[entry.id] = Pool.OfGl.Values.HELPER.getAllocableAmount(
            entry.values,
          )
        }
        return Distribution.from({
          amountToAllocate,
          statistics,
        })
      }
    }

    /**
     * With library, we first allocate the cost pool to
     * {@link common.LibraryUserCategory library user categories} based on FTEs,
     * and then for each category, we take that allocated portion of the cost
     * pool and create a {@link Allocation.Distribution} that further allocates
     * that amount to {@link common.FaFunction.Category IDC categories} based on
     * the {@link common.LibraryUserCategory} and/or associated payroll values
     *
     * For example, if Library has $100, $50 (50%) of that might get allocated
     * to {@link common.LibraryUserCategory.Faculty}, and of that $50, $30 (60%)
     * might get allocated to
     * {@link common.FaFunction.Category.Standard.Code.Instruction} and $20
     * (40%) to
     * {@link common.FaFunction.Category.Standard.Code.OrganizedResearch}
     */
    export type UsingLib = Support<Type.Lib>
    export namespace UsingLib {
      /**
       * Represents two-layers of {@link Allocation}: first by
       * {@link common.LibraryUserCategory}, and then by
       * {@link common.FaFunction.Category}
       *
       * @see {@link UsingLib}, {@link Values.PerLibraryUserCategory}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export type Values = Record<
        common.LibraryUserCategory,
        Values.PerLibraryUserCategory
      >
      export namespace Values {
        /**
         * Combines/sums multiple {@link UsingLib.Values} object into one
         * {@link UsingLib.Values} object
         */
        export function combine(
          ...valuesList: readonly Readonly.Deep<Values>[]
        ): Values {
          // Iterates through each LibraryUserCategory and combines the
          // allocations & distributions from all given values for that
          // LibraryUserCategory
          return common.LibraryUserCategory.ALL.reduce<Values>(
            (combinedValues, libraryUserCategory) =>
              Object.assign(combinedValues, {
                [libraryUserCategory]: {
                  // Combines all FTE-based allocations to this
                  // LibraryUserCategory
                  ...Allocation.Helper.INSTANCE.combine(
                    ...valuesList.map(values => values[libraryUserCategory]),
                  ),
                  // Combines all S&W-based distributions from this
                  // LibraryUserCategory
                  ...Allocation.Distribution.combine(
                    ...valuesList.map(values => values[libraryUserCategory]),
                  ),
                } satisfies Values.PerLibraryUserCategory,
              }),
            {} as Values,
          )
        }

        /**
         * Represents an {@link Allocation} of the
         * {@link common.FaFunction.Category.Standard.Code.Library} pool total
         * to a specific {@link common.LibraryUserCategory}, as well as the
         * {@link Allocation.Distribution} of that
         * {@link common.LibraryUserCategory} to other
         * {@link common.FaFunction.Category}
         */
        export type PerLibraryUserCategory = Allocation /* statistic = FTEs */ &
          Distribution /* statistic = S&W (i.e. payroll expense amount) */
        export namespace PerLibraryUserCategory {
          /**
           * Returns all {@link Values.PerLibraryUserCategory} in the given
           * {@link Values}
           */
          export function allIn(
            values: Readonly.Deep<Values>,
          ): readonly PerLibraryUserCategory[] {
            return common.LibraryUserCategory.ALL.map(
              libraryUserCategory => values[libraryUserCategory],
            )
          }

          /**
           * Used by {@link getAllowedFaCategoryIdSet}
           */
          const ALLOWED_FA_CATEGORY_ID_SET_BY_LIBRARY_USER_CATEGORY: Partial<
            Record<
              common.LibraryUserCategory,
              ReadonlySet<common.FaFunction.Category.Id>
            >
          > = {
            [common.LibraryUserCategory.Student]: new Set([
              common.FaFunction.Category.Standard.Id.of('Instruction'),
            ]),
            [common.LibraryUserCategory.Faculty]: new Set([
              common.FaFunction.Category.Standard.Id.of('Instruction'),
              common.FaFunction.Category.Standard.Id.of('OrganizedResearch'),
              common.FaFunction.Category.Standard.Id.of(
                'OtherSponsoredActivities',
              ),
            ]),
            [common.LibraryUserCategory.Other]: new Set([
              common.FaFunction.Category.Standard.Id.of(
                'OtherInstitutionalActivities',
              ),
            ]),
          }
          /**
           * Certain {@link common.LibraryUserCategory} are only allowed to
           * allocate to certain {@link common.FaFunction.Category.Id}. This
           * function returns sets containg such
           * {@link common.FaFunction.Category.Id}, or `undefined` for
           * {@link common.LibraryUserCategory} that have no constraints
           *
           * @see {@link ignoresSwStatistics}
           */
          export function getAllowedFaCategoryIdSet(
            libraryUserCategory: common.LibraryUserCategory,
          ): ReadonlySet<common.FaFunction.Category.Id> | undefined {
            return ALLOWED_FA_CATEGORY_ID_SET_BY_LIBRARY_USER_CATEGORY[
              libraryUserCategory
            ]
          }

          /**
           * In some cases, {@link getAllowedFaCategoryIdSet} will return a set
           * containing only one {@link common.FaFunction.Category.Id}. In those
           * cases, the S&W statistics don't matter. The
           * {@link Allocation.Distribution} component of
           * {@link UsingLib.Values.PerLibraryUserCategory} should always
           * allocate 100% to the single {@link common.FaFunction.Category.Id}
           */
          export function ignoresSwStatistics(
            libraryUserCategory: common.LibraryUserCategory,
          ): boolean {
            return getAllowedFaCategoryIdSet(libraryUserCategory)?.size === 1
          }
        }
      }

      /**
       * Gets the total {@link Distribution} represented by the given
       * {@link Support.UsingLib.Values}
       *
       * @see {@link Support.Values.getTotalDistribution}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function getTotalDistribution(
        values: Readonly.Deep<Values>,
      ): Distribution {
        return Distribution.combine.withCurrencyForStatistics(
          ...Values.PerLibraryUserCategory.allIn(values),
        )
      }

      /**
       * Gets the total {@link Allocation} of the
       * {@link common.FaFunction.Category.Standard.Code.Library} pool total
       * across all {@link common.LibraryUserCategory}
       *
       * Hopefully, the {@link Allocation.ValueType.Percent} is 100% and the
       * {@link Allocation.ValueType.Currency} is the same as the
       * {@link common.FaFunction.Category.Standard.Code.Library} pool total
       */
      export function getTotalFteAllocation(
        values: Readonly.Deep<Values>,
      ): Allocation {
        return Allocation.Helper.INSTANCE.combine(
          ...Values.PerLibraryUserCategory.allIn(values),
        )
      }

      /**
       * Gets both the total {@link Allocation} of the
       * {@link common.FaFunction.Category.Standard.Code.Library} pool total
       * across all {@link common.LibraryUserCategory} and the total
       * {@link Distribution} represented by the given
       * {@link Support.UsingLib.Values}
       *
       * @see {@link UsingLib.Values.getTotalDistribution}, {@link UsingLib.Values.getTotalFteAllocation}
       */

      export function getTotalFteAllocationAndDistribution(
        values: Readonly.Deep<Values>,
      ): Values.PerLibraryUserCategory {
        return {
          ...getTotalDistribution(values),
          ...getTotalFteAllocation(values),
        }
      }
    }

    /**
     * DA has _very_ unusual (and complex) support values
     *
     * The main values are stored by department (in the form of (in the form of
     * {@link UsingDa.Values.PerEntry})) and grouped by division (in the form of
     * {@link UsingDa.Values.PerGroup})
     *
     * Only a handful of the relevant values are actually stored in
     * {@link UsingDa.Values.PerEntry}. The rest are derived from those values,
     * and can be retrieved using the various getter functions under
     * {@link UsingDa} (e.g. {@link UsingDa.getFacultyAdministrativeAllowance})
     */
    export type UsingDa = Support<Type.Da>
    export namespace UsingDa {
      /**
       * @see {@link UsingDa}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export interface Values {
        groupEntity: core.dms.models.Entity.Id
        groups: readonly Values.PerGroup[]
      }
      export namespace Values {
        /**
         * Represents a division, with the {@link PerGroup.entries} representing a
         * department with {@link Values.PerEntry}
         */
        export type PerGroup = core.common.Identified<core.dms.data.Record.Id> &
          RowBasedReport.Entry.Group<PerEntry> & {
            entryEntity: core.dms.models.Entity.Id
            entries: readonly PerGroup.Entry[]
          }
        export namespace PerGroup {
          /**
           * An entry of {@link Values.PerGroup}
           */
          // eslint-disable-next-line @typescript-eslint/no-shadow
          export type Entry = core.common.Identified<core.dms.data.Record.Id> &
            RowBasedReport.Entry<PerEntry>
        }

        /**
         * Represents a department with the essential values needed for the DA
         * calculation
         *
         * NOTE: there are a lot of derived values that can be retrieved using
         * the various getter functions under {@link UsingDa} (e.g.
         * {@link UsingDa.getFacultyAdministrativeAllowance})
         */
        export interface PerEntry extends Distribution {
          /**
           * Ratio of {@link common.DaCategory.GeneralSupport} to
           * {@link common.DaCategory.Faculty} amounts for certain direct IDC
           * categories
           *
           * @example
           * const generalSupportAmountForApplicableFaCategories = $100
           * const facultyAmountForApplicableFaCategories = $500
           * const dceRatio = (
           *   generalSupportAmountForApplicableFaCategories
           *   / facultyAmountForApplicableFaCategories
           * ) // 20%
           */
          dceRatio: number
          /**
           * Portion of the natural pool of the division that is allocated to
           * this department
           *
           * Statistic is equal to the total statistic of the
           * {@link Distribution} of this department
           */
          deansAllocation: Allocation
          /**
           * The DA calculation requires information about the Instruction pool
           * in the form of {@link Pool.OfGl.Values}. It is worth noting that
           * part of the DA calculation is remapping some Instruction amounts
           * to DA. The values in this property reflect Instruction _before_
           * that remapping is done
           *
           * NOTE: the {@link common.MtdcCategory.NonSalary} amount is also known as
           * `Other Than Personnel Services (OTPS)`
           */
          instructionOriginalAmounts: Pool.OfGl.Values
          /**
           * Payroll expense amounts associated with INST FA Category split by
           * {@link common.DaCategory.ForEmployee}
           *
           * @see {@link InstructionPayrollAmountsByDaCategory}
           */
          instructionPayrollAmountsByDaCategory: InstructionPayrollAmountsByDaCategory
        }

        /**
         * Payroll expense amounts that are mapped to INST split by
         * {@link common.DaCategory.ForEmployee}, used for various calculations
         *
         * NOTE: to get the total payroll expense amount, use
         * {@link InstructionPayrollAmountsByDaCategory.getTotalAmount}
         */
        export type InstructionPayrollAmountsByDaCategory = Record<
          common.DaCategory.ForEmployee,
          number
        >
        export namespace InstructionPayrollAmountsByDaCategory {
          /**
           * {@link NumberObjectHelper} for
           * {@link InstructionPayrollAmountsByDaCategory}
           */
          // eslint-disable-next-line @typescript-eslint/no-shadow
          export class Helper extends NumberObjectHelper<InstructionPayrollAmountsByDaCategory> {
            /** Singleton of {@link Helper} */
            public static readonly INSTANCE = new Helper()

            private constructor() {
              super({
                keys: common.DaCategory.ForEmployee.ALL,
              })
            }

            /** @see {@link NumberObjectHelper} */
            public override zero(): InstructionPayrollAmountsByDaCategory {
              return {
                [common.DaCategory.BusinessOfficer]: 0,
                [common.DaCategory.Faculty]: 0,
                [common.DaCategory.GeneralSupport]: 0,
              }
            }
          }

          /**
           * Total payroll expense amount stored in {@link InstructionPayrollAmountsByDaCategory}
           */
          export function getTotalAmount(
            totalsByDaCategory: Readonly<InstructionPayrollAmountsByDaCategory>,
          ): number {
            return common.DaCategory.ForEmployee.ALL.reduce(
              (grandTotal, daCategory) =>
                grandTotal + totalsByDaCategory[daCategory],
              0,
            )
          }
        }
      }

      /**
       * Gets the total {@link Distribution} represented by the given
       * {@link Support.UsingDa.Values}
       *
       * @see {@link Support.Values.getTotalDistribution}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function getTotalDistribution(
        values: Readonly.Deep<Values>,
      ): Distribution {
        return Distribution.combine(
          ...values.groups.flatMap(group =>
            group.entries.map(entry => entry.values),
          ),
        )
      }

      /* ----------------------------- Salary & Fringe ---------------------------- */

      /**
       * Returns the sum of the salaries (i.e. payroll expense) and fringe
       * amounts that are associated with the given
       * {@link common.DaCategory.ForEmployee}
       *
       * @see {@link getTotalSalariesAndFringe}
       */
      export function getTotalSalariesAndFringeForDaCategory(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Readonly.Deep<
          Pick<
            Values.PerEntry,
            | 'instructionOriginalAmounts'
            | 'instructionPayrollAmountsByDaCategory'
          >
        >,
        daCategory: common.DaCategory.ForEmployee,
      ): number {
        const totalInstructionBasePayrollExpenseAmount =
          Values.InstructionPayrollAmountsByDaCategory.getTotalAmount(
            values.instructionPayrollAmountsByDaCategory,
          )
        const instructionBasePayrollExpenseAmountForDaCategory =
          values.instructionPayrollAmountsByDaCategory[daCategory]
        return (
          instructionBasePayrollExpenseAmountForDaCategory +
          // Fringe is portioned out based on the ratio of this payroll expense
          // amount to the total payroll expense amount
          (totalInstructionBasePayrollExpenseAmount === 0
            ? // Prevents divide by zero error
              0
            : values.instructionOriginalAmounts[common.MtdcCategory.Fringe] *
              (instructionBasePayrollExpenseAmountForDaCategory /
                totalInstructionBasePayrollExpenseAmount))
        )
      }

      /**
       * Returns the sum of the salaries (i.e. payroll expense) and fringe
       * amounts
       *
       * @see {@link getTotalSalariesAndFringeForDaCategory}
       */
      export function getTotalSalariesAndFringe(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Parameters<typeof getTotalSalariesAndFringeForDaCategory>[0],
      ): number {
        return common.DaCategory.ForEmployee.ALL.reduce(
          (total, daCategory) =>
            total + getTotalSalariesAndFringeForDaCategory(values, daCategory),
          0,
        )
      }

      /**
       * Government has set this at 3.6%
       */
      const FACULTY_ADMINISTRATIVE_ALLOWANCE_PERCENT = 0.036
      /**
       * Returns the Faculty Administrative Allowance (FAA), as specified by
       * government standards
       *
       * @see {@link getFacultyPayrollAmountMinusAdministrativeAllowance}
       */
      export function getFacultyAdministrativeAllowance(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Parameters<typeof Distribution.getTotalAllocation>[0] &
          Parameters<typeof getTotalSalariesAndFringeForDaCategory>[0],
      ): number {
        return Math.min(
          Distribution.getTotalAllocation(values)['#'] *
            FACULTY_ADMINISTRATIVE_ALLOWANCE_PERCENT,
          // Amount is limited to be no greater than the faculty payroll expense
          // amount
          getTotalSalariesAndFringeForDaCategory(
            values,
            common.DaCategory.Faculty,
          ),
        )
      }

      /**
       * Returns the faculty payroll expense amount, minus the Faculty
       * Administrative Allowance (FAA)
       *
       * @see {@link getFacultyAdministrativeAllowance}
       */
      export function getFacultyPayrollAmountMinusAdministrativeAllowance(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Parameters<typeof getTotalSalariesAndFringeForDaCategory>[0] &
          Parameters<typeof getFacultyAdministrativeAllowance>[0],
      ): number {
        return (
          getTotalSalariesAndFringeForDaCategory(
            values,
            common.DaCategory.Faculty,
          ) - getFacultyAdministrativeAllowance(values)
        )
      }

      /**
       * Computes the Instruction amount for General Support, by using
       * {@link Values.PerEntry.dceRatio} applied to
       * {@link getFacultyPayrollAmountMinusAdministrativeAllowance}
       *
       * @see {@link getGeneralSupportDaAmount}
       */
      export function getGeneralSupportInstructionAmount(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Readonly.Deep<Pick<Values.PerEntry, 'dceRatio'>> &
          Parameters<
            typeof getFacultyPayrollAmountMinusAdministrativeAllowance
          >[0] &
          Parameters<typeof getTotalSalariesAndFringeForDaCategory>[0],
      ): number {
        return Math.min(
          getFacultyPayrollAmountMinusAdministrativeAllowance(values) *
            values.dceRatio,
          // Amount is limited to be no greater than the total general support
          // amount
          getTotalSalariesAndFringeForDaCategory(
            values,
            common.DaCategory.GeneralSupport,
          ),
        )
      }

      /**
       * Computes the DA amount for General Support, which is just the remainder
       * of {@link Values.PerEntry.payrollAmountsByDaCategory} for
       * {@link common.DaCategory.GeneralSupport} after taking out
       * {@link getGeneralSupportInstructionAmount}
       *
       * @see {@link getGeneralSupportInstructionAmount}
       */
      export function getGeneralSupportDaAmount(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Parameters<typeof getTotalSalariesAndFringeForDaCategory>[0] &
          Parameters<typeof getGeneralSupportInstructionAmount>[0],
      ): number {
        return Math.max(
          getTotalSalariesAndFringeForDaCategory(
            values,
            common.DaCategory.GeneralSupport,
          ) - getGeneralSupportInstructionAmount(values),
          // Cannot be negative
          0,
        )
      }

      /**
       * When it comes to Salaries and Wages (S&W), Instruction gets the Faculty
       * amount, minus the FAA, plus its computed General Support amount, while
       * DA gets the FAA plus its computed General Support amount, plus all of
       * the Business Officer amount
       *
       * @see {@link getSalariesAndWagesDaAmount}
       */
      export function getSalariesAndWagesInstructionAmount(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Parameters<
          typeof getFacultyPayrollAmountMinusAdministrativeAllowance
        >[0] &
          Parameters<typeof getGeneralSupportInstructionAmount>[0],
      ): number {
        return Math.max(
          getFacultyPayrollAmountMinusAdministrativeAllowance(values) +
            getGeneralSupportInstructionAmount(values),
          // Cannot be negative
          0,
        )
      }

      /**
       * When it comes to Salaries and Wages (S&W), DA gets the FAA plus its
       * computed General Support amount, plus all of the Business Officer
       * amount, while Instruction gets the Faculty amount, minus the FAA, plus
       * its computed General Support amount
       *
       * @see {@link getSalariesAndWagesInstructionAmount}
       */
      export function getSalariesAndWagesDaAmount(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Parameters<typeof getGeneralSupportDaAmount>[0] &
          Parameters<typeof getFacultyAdministrativeAllowance>[0] &
          Parameters<typeof getTotalSalariesAndFringeForDaCategory>[0],
      ): number {
        return (
          getFacultyAdministrativeAllowance(values) +
          getGeneralSupportDaAmount(values) +
          getTotalSalariesAndFringeForDaCategory(
            values,
            common.DaCategory.BusinessOfficer,
          )
        )
      }

      /**
       * Just the sum of {@link getSalariesAndWagesInstructionAmount} and {@link getSalariesAndWagesDaAmount}
       */
      export function getSalariesAndWagesTotal(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Parameters<typeof getSalariesAndWagesDaAmount>[0] &
          Parameters<typeof getSalariesAndWagesInstructionAmount>[0],
      ): number {
        return (
          getSalariesAndWagesDaAmount(values) +
          getSalariesAndWagesInstructionAmount(values)
        )
      }

      /**
       * Simply {@link getSalariesAndWagesInstructionAmount} divided by
       * {@link getSalariesAndWagesTotal}
       *
       * @see {@link getSalariesAndWagesDaPercent}
       */
      export function getSalariesAndWagesInstructionPercent(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Parameters<typeof getSalariesAndWagesInstructionAmount>[0] &
          Parameters<typeof getSalariesAndWagesTotal>[0],
      ): number {
        const swTotal = getSalariesAndWagesTotal(values)
        return swTotal === 0
          ? 0
          : getSalariesAndWagesInstructionAmount(values) / swTotal
      }

      /**
       * Simply {@link getSalariesAndWagesDaAmount} divided by
       * {@link getSalariesAndWagesTotal}
       *
       * @see {@link getSalariesAndWagesInstructionPercent}
       */
      export function getSalariesAndWagesDaPercent(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Parameters<typeof getSalariesAndWagesDaAmount>[0] &
          Parameters<typeof getSalariesAndWagesTotal>[0],
      ): number {
        const swTotal = getSalariesAndWagesTotal(values)
        return swTotal === 0 ? 0 : getSalariesAndWagesDaAmount(values) / swTotal
      }

      /* ------------------------------- Non-Salary ------------------------------- */

      /**
       * Simply the total Instruction non-salary amount (i.e.
       * {@link Values.PerEntry.instructionOriginalAmounts}[{@link common.MtdcCategory.NonSalary}])
       * minus {@link getOtpsDa}
       *
       * Generally, this should be proportional to
       * {@link getSalariesAndWagesInstructionPercent}, as long as
       * {@link getSalariesAndWagesInstructionPercent} +
       * {@link getSalariesAndWagesDaPercent} = 100%. The only case where this
       * should not be true is when both
       * {@link getSalariesAndWagesInstructionPercent} and
       * {@link getSalariesAndWagesDaPercent} return 0%, in which case this
       * function will just return the total Instruction non-salary amount
       */
      export function getOtpsInstruction(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Readonly.Deep<
          Pick<Values.PerEntry, 'instructionOriginalAmounts'>
        > &
          Parameters<typeof getOtpsDa>[0],
      ): number {
        return (
          values.instructionOriginalAmounts[common.MtdcCategory.NonSalary] -
          getOtpsDa(values)
        )
      }

      /**
       * Portion of the total Instruction non-salary amount (i.e.
       * {@link Values.PerEntry.instructionOriginalAmounts}[{@link common.MtdcCategory.NonSalary}])
       * that corresponds to {@link getSalariesAndWagesDaPercent}
       */
      export function getOtpsDa(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Readonly.Deep<
          Pick<Values.PerEntry, 'instructionOriginalAmounts'>
        > &
          Parameters<typeof getSalariesAndWagesDaPercent>[0],
      ): number {
        return (
          values.instructionOriginalAmounts[common.MtdcCategory.NonSalary] *
          getSalariesAndWagesDaPercent(values)
        )
      }

      /* --------------------Instruction Post DA Reduction ----------------------*/
      /**
       * Instruction amount after reducing DA subtotal
       */
      export function getInstructionAfterDaSubtotal(
        values: Parameters<typeof getDaSubtotal>[0],
      ): number {
        return (
          (values.allocations[
            common.FaFunction.Category.Standard.Id.of('Instruction')
          ]?.['#'] ?? 0) - getDaSubtotal(values)
        )
      }
      /* --------------------------------- Totals --------------------------------- */

      /**
       * Simply the sum of {@link getSalariesAndWagesDaAmount} (salary + fringe)
       * and {@link getOtpsDa} (non-salary)
       *
       * This represents the amount of money being remapped from Instruction to
       * DA, also known as the "scoop out" amount
       */
      export function getDaSubtotal(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Parameters<typeof getSalariesAndWagesDaAmount>[0] &
          Parameters<typeof getOtpsDa>[0],
      ): number {
        return getSalariesAndWagesDaAmount(values) + getOtpsDa(values)
      }

      /**
       * Simply the dollar amount in {@link Values.PerEntry.deansAllocation}
       * (i.e. natural pool amount for this department) plus
       * {@link getDaSubtotal} (scoop out from Instruction to DA)
       */
      export function getDaTotal(
        // NOTE: picking just what we need makes the function more flexible
        // (e.g. we can use it on certain partial values)
        values: Readonly.Deep<Pick<Values.PerEntry, 'deansAllocation'>> &
          Parameters<typeof getDaSubtotal>[0],
      ): number {
        return getDaSubtotal(values) + values.deansAllocation.$
      }
    }
  }

  const FA_CATEGORY_IDS_THAT_NEVER_RECEIVE_ALLOCATION = [
    common.FaFunction.Category.Standard.Id.of('BuildingDepreciation'),
    common.FaFunction.Category.Standard.Id.of('EquipmentDepreciation'),
    common.FaFunction.Category.Standard.Id.of('Interest'),
  ]
  export const isFaCategoryIdThatNeverReceiveAllocation =
    TypeChecker.Factory.oneOf(FA_CATEGORY_IDS_THAT_NEVER_RECEIVE_ALLOCATION)
}
